"use client";

import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import {
    AlertBasic,
    Button,
    Form,
    FormCheckbox,
    FormProps,
    LoadingSkeleton,
    Markdown,
    Text,
} from "@wojo/ui";
import { TipTap } from "components/tip-tap";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
    MeBookingTripWithBookingDocument,
    MeBookingTripWithBookingQueryVariables,
    MyAccountBasicMyTripFragment,
    TermFragment,
    TermType,
    useAcceptGcDepartureTermsMutation,
    useAcceptTripTermsMutation,
} from "generated/graphql";
import { Suspense, useState } from "react";
import { useForm } from "react-hook-form";
import styles from "./ReviewTermsForm.module.scss";

dayjs.extend(utc);

type FormFields = {
    acceptedTerms: boolean;
};

type ReviewTermsFormProps = {
    navigateToSuccess: () => void;
    term: TermFragment;
    titleId: string;
    trip: MyAccountBasicMyTripFragment;
};

export const ReviewTermsForm: React.FC<ReviewTermsFormProps> = ({
    navigateToSuccess,
    term,
    titleId,
    trip,
}) => {
    const i18n =
        useI18n("account").account.termsAndConditionsModal.reviewTermsForm;
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [acceptTripTerms] = useAcceptTripTermsMutation();
    const [acceptGCDepartureTerms] = useAcceptGcDepartureTermsMutation();
    const handleSubmit: FormProps<FormFields>["onSubmit"] = async () => {
        const isGcTerms = term.termType === TermType.GroupCoordinator;
        try {
            if (isGcTerms) {
                const { data } = await acceptGCDepartureTerms({
                    variables: {
                        id: trip.booking?.gcDeparture?._id ?? "",
                        termId: term._id,
                        language:
                            process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
                    },
                    /**
                     * acceptTripTerms returns MeBooking which we want
                     * acceptGCDepartureTerms returns a GCDeparture
                     * we have to get the MeBooking ourselves
                     */
                    refetchQueries: [
                        {
                            query: MeBookingTripWithBookingDocument,
                            variables: {
                                id: trip._id,
                                language:
                                    process.env
                                        .NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
                            } satisfies MeBookingTripWithBookingQueryVariables,
                        },
                    ],
                    awaitRefetchQueries: true,
                });
                if (data?.acceptGCDepartureTerms.__typename !== "GCDeparture") {
                    throw new Error("Failed to accept GC departure terms");
                }
            } else {
                const { data } = await acceptTripTerms({
                    variables: {
                        bookingId: trip.booking?._id ?? "",
                        tripId: trip._id,
                        termId: term._id,
                    },
                });
                if (data?.acceptTripTerms.__typename !== "Booking") {
                    throw new Error("Failed to accept general terms");
                }
            }
            gtmSendEvent({
                event: "track",
                ga4Tracking: {
                    ga4_object: isGcTerms ? "GC_FORCED_TERMS" : "FORCED_TERMS",
                    ga4_action: "ACCEPTED",
                    tourCode: trip.departure?.tourCode ?? "",
                },
            });
            navigateToSuccess();
        } catch {
            setErrorMessage(i18n.submitFailedError.l());
        }
    };

    const form = useForm<FormFields>({
        defaultValues: {
            acceptedTerms: false,
        },
    });

    return (
        <Form<FormFields> form={form} onSubmit={handleSubmit}>
            <div className={styles["header-wrapper"]}>
                <Text.Heading
                    id={titleId}
                    order="3"
                    tag="h1"
                    style={{ marginBottom: "var(--g-spacing-md)" }}
                >
                    {i18n.title.l()}
                </Text.Heading>
                <Markdown
                    markdown={i18n.description.l({
                        departureDate: dayjs
                            .utc(trip.departure?.departureDate)
                            .format(i18n.departureDateFormat.l()),
                        tourName:
                            trip.booking?.gcDeparture?.micrositeContent
                                ?.title ||
                            trip.departure?.tourContent?.name ||
                            i18n.tourNameFallback.l(),
                    })}
                />
            </div>
            <Suspense
                fallback={
                    <LoadingSkeleton
                        accessibleText={i18n.loadingTermsAndConditions.l()}
                        height="500"
                    />
                }
            >
                <div className={styles["terms-wrapper"]}>
                    <TipTap json={term.content} />
                </div>
                <div className={styles["certification-wrapper"]}>
                    <Text.Body
                        emphasis="2"
                        style={{
                            marginBottom: "var(--g-spacing-lg)",
                        }}
                    >
                        {i18n.certificationHeading.l()}
                    </Text.Body>
                    <Text.Body>{term.certificationText}</Text.Body>
                </div>
                {errorMessage && (
                    <div className={styles["error-wrapper"]}>
                        <AlertBasic status="alert" title={errorMessage} />
                    </div>
                )}
                <div className={styles["checkbox-wrapper"]}>
                    <FormCheckbox
                        name="acceptedTerms"
                        label={i18n.acceptCheckboxLabel.l()}
                        validation={{
                            required: i18n.acceptCheckboxRequired.l(),
                        }}
                    />
                </div>
                <div className={styles["accept-wrapper"]}>
                    <Button
                        variant="primary"
                        loading={form.formState.isSubmitting}
                    >
                        {i18n.submitButton.l()}
                    </Button>
                </div>
            </Suspense>
        </Form>
    );
};
